import { Routes } from '@angular/router';
import { autoLoginPartialRoutesGuard } from 'angular-auth-oidc-client';
import { authCallbackGuard } from './core/guards/auth-callback.guard';
import { languageGuard } from './core/guards/language.guard';
import { saveRedirectGuard } from './core/guards/save-redirect.guard';
import { userDataGuard } from './core/guards/user-data.guard';
import { companyGuard } from './core/guards/company.guard';
import { mojitoAccessGuard } from './core/guards/mojito-access.guard';
import { MojitoAccessApiService } from './core/services/mojito-access-api.service';
import {
  TranslationService,
  loadTranslations,
} from './core/services/translation.service';
import { CompanyContextService } from './layouts/main-layout/services/company-context.service';
import { provideLocale } from './core/providers/locale.providers';

export const appRoutes: Routes = [
  {
    // The guard always returns a UrlTree, so nothing is ever rendered here.
    // checkAuth() already ran in the bootstrap initializer.
    path: 'auth/callback',
    canActivate: [authCallbackGuard],
    children: [],
  },
  {
    path: 'auth/oauth-callback',
    canActivate: [autoLoginPartialRoutesGuard, userDataGuard],
    loadComponent: () =>
      import('./core/components/oauth-callback.component').then(
        (c) => c.OAuthCallbackComponent,
      ),
  },
  {
    // Declared before ':lang' on purpose: ':lang' is an unconstrained
    // single-segment param, so it would otherwise match 'unauthorized',
    // get rejected by languageGuard, and bounce to /es -> the auth guard.
    // Deliberately outside the guarded tree, and it carries its own
    // TranslationService because it is not a child of ':lang'.
    path: 'unauthorized',
    providers: [TranslationService, ...provideLocale()],
    resolve: { _t: loadTranslations('common') },
    loadComponent: () =>
      import('./core/components/unauthorized').then(
        (c) => c.UnauthorizedComponent,
      ),
  },
  { path: '', pathMatch: 'full', redirectTo: 'es' },
  {
    path: ':lang',
    canActivate: [
      languageGuard,
      saveRedirectGuard,
      autoLoginPartialRoutesGuard,
      userDataGuard,
    ],
    providers: [TranslationService, ...provideLocale()],
    resolve: { _t: loadTranslations('common') },
    children: [
      {
        path: 'select-company',
        loadChildren: () =>
          import('./features/company-selection/company-selection.routes').then(
            (r) => r.companySelectionRoutes,
          ),
      },
      {
        // Entrada desde «Global Analytics» de Mojito360. Va antes de
        // ':companyCode' para que este no se trague 'from-mojito', y dentro de
        // ':lang' para que el enlace pase por el login y se recuerde durante el
        // ida y vuelta del SSO. El guard siempre redirige, así que aquí no se
        // pinta nada.
        path: 'from-mojito/:mojitoCompanyId',
        canActivate: [mojitoAccessGuard],
        providers: [MojitoAccessApiService],
        children: [],
      },
      {
        path: ':companyCode',
        canActivate: [companyGuard],
        providers: [CompanyContextService],
        loadChildren: () =>
          import('./layouts/main-layout/main-layout.routes').then(
            (r) => r.mainLayoutRoutes,
          ),
      },
      { path: '', redirectTo: 'select-company', pathMatch: 'full' },
    ],
  },
  { path: '**', redirectTo: 'es' },
];
